import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { PageHeader, Loader, EmptyState, fmtDate } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollText, Search } from "lucide-react";

export default function AuditLog() {
  const [logs, setLogs] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/audit").then((r) => setLogs(r.data)).catch(() => setLogs([])); }, []);
  if (!logs) return <Loader label="Loading audit trail" />;

  const filtered = logs.filter((l) =>
    !q || `${l.action} ${l.actor_name} ${l.entity_type}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <PageHeader
        overline="Governance & compliance"
        title="Audit Trail"
        description="Append-only record of all material actions across the platform."
      />
      <div className="mb-4 relative max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by action, actor, entity…" className="pl-9 rounded-md" data-testid="audit-search-input" />
      </div>
      {filtered.length === 0 ? (
        <EmptyState icon={ScrollText} title="No audit events" description="Material actions will be recorded here." />
      ) : (
        <div className="bg-card border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs uppercase tracking-wider">Timestamp</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Actor</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Action</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Entity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((l, i) => (
                <motion.tr key={l.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.01, 0.3) }} className="border-b border-border/60" data-testid={`audit-row-${l.id}`}>
                  <TableCell className="py-2.5 text-xs font-mono text-muted-foreground">{fmtDate(l.timestamp)}</TableCell>
                  <TableCell className="py-2.5 text-sm">{l.actor_name || "—"}<div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{l.actor_role}</div></TableCell>
                  <TableCell className="py-2.5"><span className="font-mono text-xs bg-slate-900 text-white rounded px-1.5 py-0.5">{l.action}</span></TableCell>
                  <TableCell className="py-2.5 text-xs font-mono text-muted-foreground">{l.entity_type}{l.entity_id ? ` · ${l.entity_id.slice(-6)}` : ""}</TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
